/* eslint-disable */

/**
 * Task Notifications — Vuex module
 *
 * Listens to the Firestore `notifications` collection for new `type='TASK'`
 * documents targeting the current employee, and fires sound + desktop push
 * via the detection helper in src/utils/taskNotificationDetection.
 *
 * Parallel to the existing chat / SMS notification path in Sidebar.vue. Kept
 * as a standalone module so the general `notifications` store doesn't have to
 * grow task-specific firing concerns.
 *
 * Design doc: docs/plans/2026-04-16-notification-settings-design.md
 */

import {
  collection,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { safeSnapshot } from '@/store/firestoreListen';
import Push from 'push.js';
import { detectTaskNotifications } from '@/utils/taskNotificationDetection';
import { isEnabled, getSound } from '@/utils/notificationPrefs';

export default {
  namespaced: true,
  state: {
    listener: null,
    prevSeenIds: {},
    isFirstSnapshot: true,
    newTaskTimestamp: 0,
    latestTaskNotification: null,
  },
  mutations: {
    setListener(state, unsub) { state.listener = unsub; },
    setPrevSeenIds(state, map) { state.prevSeenIds = map; },
    setIsFirstSnapshot(state, v) { state.isFirstSnapshot = v; },
    setNewTaskTimestamp(state, t) { state.newTaskTimestamp = t; },
    setLatestTaskNotification(state, n) { state.latestTaskNotification = n; },
  },
  actions: {
    /**
     * Attach the Firestore listener. Safe to call multiple times — duplicate
     * calls detach the previous listener first.
     */
    attachListener({ state, commit, rootState, dispatch }, { employeeID }) {
      if (!employeeID) return;
      if (!rootState.db) {
        try { console.warn('[taskNotifications] rootState.db missing; cannot attach'); } catch(_) {}
        return;
      }

      // Detach any existing listener (hot reload, user switch, etc.)
      if (state.listener) {
        try { state.listener(); } catch(_) {}
        commit('setListener', null);
      }
      commit('setPrevSeenIds', {});
      commit('setIsFirstSnapshot', true);

      // Reuse the existing notifications/q2 composite index (audience + employeeID +
      // unreadList array-contains + createdAt desc) so we don't have to add a new
      // Firestore index just for this listener. We filter for type='TASK' client-side.
      // Every TASK notification has the assignee in unreadList, so this doesn't miss any.
      const q = query(
        collection(rootState.db, 'notifications'),
        where('audience', '==', 'EMPLOYEE'),
        where('employeeID', '==', employeeID),
        where('unreadList', 'array-contains', employeeID),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = safeSnapshot(q, (snap) => {
        const notifications = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          // Client-side filter to the types we actually fire for. The detection
          // helper also filters, but pre-filtering keeps the seen-ID map small.
          if (data.type !== 'TASK' && data.type !== 'KANBAN') return;
          notifications.push({
            id: docSnap.id,
            type: data.type,
            employeeID: data.employeeID,
            text: data.text,
            createdAt: data.createdAt,
          });
        });

        const userID = rootState.customer?.userID;
        const gates = {
          TASK: isEnabled(userID, 'task'),
          KANBAN: isEnabled(userID, 'kanban'),
        };

        const { nextSeenIds, fires } = detectTaskNotifications({
          notifications,
          prevSeenIds: state.prevSeenIds,
          isFirstSnapshot: state.isFirstSnapshot,
          currentUserID: employeeID,
          gates,
        });

        commit('setPrevSeenIds', nextSeenIds);
        if (state.isFirstSnapshot) commit('setIsFirstSnapshot', false);

        if (fires.length > 0) {
          // Fire the newest one (detection can return many; we coalesce to the newest
          // to avoid a rapid-fire barrage when a manager bulk-assigns many items).
          const latest = fires[0];
          const soundFile = getSound(userID, latest.type === 'KANBAN' ? 'kanban' : 'task');

          dispatch('fireTaskAlert', {
            text: latest.text,
            soundFile,
            type: latest.type,
          });

          commit('setLatestTaskNotification', latest);
          commit('setNewTaskTimestamp', Date.now());
        }
      }, {
        source: 'taskNotifications/employee',
        onError: (err) => {
          try { console.error('[taskNotifications] onSnapshot error:', err); } catch(_) {}
        },
      });

      commit('setListener', unsubscribe);
      commit('customer/addFirestoreListener', unsubscribe, { root: true });
    },

    /**
     * Play sound + fire desktop push. Kept as an action so it's mockable
     * and so Layout.vue can override the play path if needed.
     */
    fireTaskAlert(_ctx, { text, soundFile, type }) {
      try {
        const audio = new Audio('/sounds/' + soundFile);
        const p = audio.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => { /* autoplay blocked, ignore */ });
        }
      } catch (_) { /* ignore */ }

      try {
        // Strip HTML from the notification text for the push body
        const defaultBody = type === 'KANBAN'
          ? 'You have been mentioned on a Kanban card'
          : 'You have been assigned a task';
        const body = (text || '').replace(/<[^>]*>/g, '').trim() || defaultBody;
        const title = type === 'KANBAN' ? 'New Kanban Mention' : 'New Task Assignment';
        Push.create(title, { body, timeout: 6000 });
      } catch (_) { /* permission denied or Push unavailable */ }
    },

    detach({ state, commit }) {
      if (state.listener) {
        try { state.listener(); } catch(_) {}
        commit('setListener', null);
      }
      commit('setPrevSeenIds', {});
      commit('setIsFirstSnapshot', true);
    },
  },
};
